// Core
import React, { FC, ReactElement, useRef } from 'react';
import Transition, { TransitionStatus } from 'react-transition-group/Transition';

// Styles
import Styles from './styles.module.scss';

// Instruments
import { Virtuoso } from 'react-virtuoso';
import { opacityTransitionConfig } from 'utils/transitionConfig';
import { AlwTableRow } from '../AlwTableRow';
import { AlwSpinner } from '../AlwSpinner';
import { AlienwareError } from '../AlienwareError';
import { AlwGames, AlwSupportedGamesIds } from '../../types';
import errorTriangleYellow from 'theme/assets/svg/v5/alw-contest/error-triangle-yellow.svg';
import { useAlwLeaderboard } from 'hooks/useAlwLeaderboard';

const gamesIds = {
    Fortnite: 21216,
    'League of Legends': 5426,
    Valorant: 21640,
    'Rainbow Six': 10826,
} as Record<string, AlwSupportedGamesIds>;

const gamesNames = {
    21216: 'Fortnite',
    5426: 'League of Legends',
    21640: 'Valorant',
    10826: 'Rainbow Six',
} as Record<AlwSupportedGamesIds, AlwGames>;

type AlwLeaderboardsProps = {
    className?: string;
};

export const AlwLeaderboards: FC<AlwLeaderboardsProps> = ({ className }): ReactElement => {
    const nodeRef = useRef(null);

    const {
        ladder,
        ladderFetching,
        ladderChosenGame,
        more,
        ladderError,
        setLadderChosenGame,
        fetchAlwLadder,
        loadMore,
    } = useAlwLeaderboard();

    const winnerId = ladder.find((user) => !user.reward)?.id;

    return (
        <Transition
            in
            appear
            mountOnEnter
            unmountOnExit
            timeout={opacityTransitionConfig().timeout}
            nodeRef={nodeRef}
        >
            {(state: TransitionStatus) => (
                <section
                    className={[Styles.container, className].filter(Boolean).join(' ')}
                    style={{
                        ...opacityTransitionConfig().defaultStyles,
                        ...opacityTransitionConfig().transitionStyles[state],
                    }}
                >
                    <p className={Styles.title}>2021 Alienware Game Contest</p>

                    <div className={Styles.badges}>
                        {Object.entries(gamesNames).map(([key, value]) => (
                            <div
                                className={[
                                    Styles.badge,
                                    ladderChosenGame === gamesIds[key] && Styles.chosen,
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                onClick={() => {
                                    // @ts-ignore
                                    setLadderChosenGame(key);
                                }}
                                key={key}
                            >
                                {value}
                            </div>
                        ))}
                    </div>

                    <div className={Styles.table}>
                        <div className={Styles.tableHead}>
                            <img
                                src={errorTriangleYellow}
                                alt=''
                                className={[Styles.info, Styles.hidden].filter(Boolean).join(' ')}
                            />
                            <span>Rank</span>

                            <span>Name</span>

                            <span>Prize</span>

                            <span className={Styles.points}>Points</span>
                        </div>

                        {ladderFetching && <AlwSpinner className={Styles.tableSpinner} />}

                        {ladderError ? (
                            <AlienwareError className={Styles.error} retryAction={fetchAlwLadder} />
                        ) : (
                            <Virtuoso
                                className={Styles.tableRows}
                                data={ladder}
                                overscan={200}
                                itemContent={(index, item) => (
                                    <AlwTableRow
                                        {...item}
                                        key={index}
                                        inProp={!ladderFetching}
                                        ladderChosenGame={ladderChosenGame}
                                        index={index}
                                        winner={winnerId === item.id}
                                    />
                                )}
                                endReached={() => {
                                    if (more) {
                                        loadMore();
                                    }
                                }}
                            />
                        )}
                    </div>
                </section>
            )}
        </Transition>
    );
};
